import { Injectable, inject } from '@angular/core';
import { ToastController } from '@ionic/angular';
import { Console } from '../../shared/providers/custom-logger';
import { ToastModel } from '../models/toast.model';

/**
 * Servizio per la gestione dei toast nell'applicazione.
 *
 * Fornisce metodi per mostrare toast di successo, errore, attenzione e informazione,
 * utilizzando lo Ionic ToastController.
 *
 * Questo servizio centralizza la gestione dei toast per fornire
 * un'esperienza utente coerente e una facile manutenzione.
 *
 * @example
 * // Mostra un toast di successo
 * toastService.presentaToastSuccesso('Operazione completata');
 */
@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private toastController = inject(ToastController);

  /**
   * Mostra un toast generico con le opzioni fornite.
   * @param toast_options Opzioni del toast
   */
  private async presentaToast(toast_options: ToastModel) {
    try {
      this.toastController
        .dismiss()
        .then(() => {})
        .catch(() => {})
        .finally(() => {
          Console.log("ToastService:", "closed");
        });
    } catch (e) {}

    const toast = await this.toastController.create(toast_options);
    await toast.present();
    await toast.onDidDismiss();   // attende che il toast sia chiuso
  }

  /**
   * Mostra un toast di successo.
   * @param message Messaggio da mostrare
   * @param log_label Etichetta di log opzionale
   */
  presentaToastSuccesso(message: string, log_label: string = "") {
    Console.log(log_label + " ToastService:", "presentaToastSuccesso");
    let toast_options: ToastModel = new ToastModel(message, "success");
    this.presentaToast(toast_options);
  }

  /**
   * Mostra un toast di errore.
   * @param message Messaggio da mostrare
   * @param log_label Etichetta di log opzionale
   */
  presentaToastErrore(message: string, log_label: string = "") {
    Console.log(log_label + " ToastService:", "presentaToastErrore");
    let toast_options: ToastModel = new ToastModel(message, "danger");
    this.presentaToast(toast_options);
  }

  /**
   * Mostra un toast di attenzione.
   * @param message Messaggio da mostrare
   * @param log_label Etichetta di log opzionale
   */
  presentaToastAttenzione(message: string, log_label: string = "") {
    Console.log(log_label + " ToastService:", "presentaToastAttenzione");
    let toast_options: ToastModel = new ToastModel(message, "warning");
    toast_options.cssClass = "toast-warning-class";
    this.presentaToast(toast_options);
  }
  
  /**
   * Mostra un toast informativo.
   * @param message Messaggio da mostrare
   * @param log_label Etichetta di log opzionale
   */
  presentaToastInfo(message: string, log_label: string = "") {
    Console.log(log_label + " ToastService:", "presentaToastInfo");
    let toast_options: ToastModel = new ToastModel(message, "info");
    this.presentaToast(toast_options);
  }
}
